import {PolymerElement} from '@polymer/polymer';

class KeliPromptController extends PolymerElement {
  static get is() {
    return 'keli-prompt-controller'
  }

  static get properties() {
    return {
      callback: {type: Object},
      defaultAction: {type: Object},
      element: {type: Object},
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.callback) {
      this.callback();
      this.callback = null;
    }

    this.element = null;
    this.defaultAction = null;
  }

  async elementReady(element) {
    this.element = element;

    this.element.addEventListener('keliUserChange', ({detail}) => {
      this.responseFields[detail.element.id] = detail.newValue;
    });

    this.element.addEventListener('keliAccepted', ({detail}) => {
      this.responseFields[detail.element.id] = detail.value;
    });

    this.element.addEventListener('promptTriggerDefaultAction', (event) => {
      if (this.defaultAction && this.defaultAction.activate) {
        this.defaultAction.dispatchEvent(new CustomEvent('focusElement'));
        this.defaultAction.activate(event);
      }
    });

    this.element.addEventListener('keliActionActivated', ({detail}) => {
      if (detail && detail.element && detail.element.region === 'actions') {
        if (!this.element) {
          return;
        }

        // Grab a final version of the field values
        const fields = this.element.$.controls.children;
        for (const field of fields) {
          if (field.id) {
            if (field._keliAcceptEventOverrides && field._keliAcceptEventOverrides.valueid) {
              this.responseFields[field.id] = field._keliAcceptEventOverrides.valueid;
            } else {
              this.responseFields[field.id] = field.toRawFieldValue?.(field.currentValue) ?? field.currentValue;
            }
          }
        }

        // Check for required fields
        let preventClosing = false;
        if (this.requiredFields && this.requiredFields.length && this.defaultAction === detail.element) {
          for (let i = 0; i < this.requiredFields.length; ++i) {
            if (!this.responseFields[this.requiredFields[i]]) {
              const element = this.element.$.controls.querySelector(`#${this.requiredFields[i]}`);
              element.validityState = {valid: false, message: 'Field is required.', fromUI: true};
              preventClosing = true;
            }
          }
        }
        if (preventClosing) {
          return;
        }

        this.responseFields[detail.element.id] = true;
        if (this.callback) {
          this.callback(null, this.responseFields);
          this.callback = null;
        }
        this.element.close();
      }
    });
  }

  populatePrompt(event, element) {
    if (element && !this.element) {
      this.element = element;
    }
    this.responseFields = {};
    this.requiredFields = [];
    this.callback = event && event.options && event.options.callback;

    // Set the default properties for the prompt.
    this.element.caption = (event.options && event.options.title) || '';
    this.element.text = event.text || '';

    // Set up the customer elements.
    if (event.options && event.options.elements) {
      for (let i = 0; i < event.options.elements.length; ++i) {
        this.createElement(event.options.elements[i]);
      }
    }

    this.element.focusActiveControl();
  }

  createElement(elementMeta) {
    let componentMeta = KeliInterface.registeredKeliControlTypes[elementMeta.controltype];
    if (!componentMeta) {
      console.warn('%cPrompt:', 'background: #fc0; color: #000', `Unknown control type: "${elementMeta.controltype}". Defaulting to "input".`);
      componentMeta = KeliInterface.registeredKeliControlTypes['input'];
    }

    const element = document.createElement(componentMeta.tagName);
    element.id = elementMeta.id || elementMeta.name;
    element.region = elementMeta.region;

    // Default the currentValue property to empty.
    // This solves a bug in Chromium where email fields show the Polymer binding as the default value.
    if (element._currentValueChanged && !element.currentValue) {
      element.currentValue = '';
    }

    const region = (elementMeta.region && this.element.$[elementMeta.region]) || this.element.$.controls;
    region.appendChild(element);

    // Add any attributes defined on the element.
    if (elementMeta.attributes) {
      for (const attribute in elementMeta.attributes) {
        if (!elementMeta.attributes.hasOwnProperty(attribute)) {
          continue;
        }
        // Only allow hotkeys on action buttons.
        if (attribute === 'hotkey' && elementMeta.region !== 'actions') {
          continue;
        }

        switch (attribute) {
          case 'hotkey':
            element.hotkeys = (Array.isArray(elementMeta.attributes[attribute]) ? elementMeta.attributes[attribute] : [elementMeta.attributes[attribute]]);
            element.shortcutKey = element.hotkeys[0];
            break;
          case 'displayvalue':
            element[attribute] = elementMeta.attributes[attribute];
            element.displayValue = elementMeta.attributes[attribute];
            break;
          default:
            element[attribute] = elementMeta.attributes[attribute];
        }

        if (['currentvalue', 'displayvalue', 'value'].includes(attribute)) {
          this.responseFields[element.id] = elementMeta.attributes[attribute];
        }
        if (attribute === 'required' && elementMeta.attributes[attribute] === true) {
          this.requiredFields.push(element.id);
        }
      }
    }
    if (elementMeta.region === 'actions' && componentMeta.tagName === 'keli-button') {
      element.design = 'flat';
    }
    if (elementMeta.defaultaction && !this.defaultAction) {
      this.defaultAction = element;
      if (Array.isArray(element.hotkeys)) {
        element.hotkeys.push(KeliInterface.Hotkeys.prompt.submit.keys);
      } else {
        element.hotkeys = [KeliInterface.Hotkeys.prompt.submit.keys];
      }
    }
  }
}

customElements.define(KeliPromptController.is, KeliPromptController);
export {KeliPromptController};
